import { getLanguage as getInterfaceLanguage } from '@/utils/i18n';
import { tx as translateInterfaceText } from '@/utils/i18n';
import { useLanguage } from '@/hooks/useLanguage';
import React from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Trophy, TrendingUp, Smartphone } from 'lucide-react';
import { PrimeTimesMatrix, PrimeTimeMetric } from '../../hooks/types/primeTimesTypes';
import { Badge } from '@/components/ui/badge';
import {
  calculateChampion,
  calculateTop4Windows,
  calculateDeviceBestWindows
} from '../utils/peakCalculations';
import { MetricCardHeader } from '../../common/MetricCardHeader';

interface PremiumPeakCardsProps {
  data: PrimeTimesMatrix;
  metric: PrimeTimeMetric;
}

export function PremiumPeakCards({ data, metric }: PremiumPeakCardsProps) {
  useLanguage();
  const champion = calculateChampion(data, metric);
  const otherTopWindows = calculateTop4Windows(data, metric);
  const deviceBestWindows = calculateDeviceBestWindows(data, metric);

  return (
    <div className="space-y-4">
      {/* Grid com 3 cards na mesma linha */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {/* Card 1: Horário Campeão */}
        <Card className="border border-[#E2E8FF] rounded-[12px] bg-white flex flex-col min-h-[280px]">
          <CardContent className="px-[22px] py-[16px] flex-1 flex flex-col gap-[12px]">
            <MetricCardHeader
              icon={Trophy}
              title={translateInterfaceText("Horário campeão do período")}
              subtitle={translateInterfaceText("Melhor período de performance")}
            />

            {/* Período Campeão - Badge azul claro */}
            <div className="flex items-center justify-between">
              <span className="text-[12px] font-normal text-[#6B7280]">{translateInterfaceText("Período campeão")}</span>
              <span className="inline-flex px-3 py-1 rounded-full bg-[#E9F0FF] text-[12px] font-medium text-[#2D6CF6]">
                {champion.weekdayLabel} • {champion.hourLabel}
              </span>
            </div>

            <div className="flex flex-col gap-[8px]">
              <div className="flex items-center justify-between">
                <span className="text-[12px] font-normal text-[#6B7280]">{translateInterfaceText("Total de Plays Únicos")}</span>
                <span className="text-[13px] font-medium text-[#0F172A]">
                  {champion.playsUnique.toLocaleString(getInterfaceLanguage())}
                </span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-[12px] font-normal text-[#6B7280]">{translateInterfaceText("Total de Visualizações Únicas")}</span>
                <span className="text-[13px] font-medium text-[#0F172A]">
                  {champion.viewsUnique.toLocaleString(getInterfaceLanguage())}
                </span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-[12px] font-normal text-[#6B7280]">{translateInterfaceText("Dispositivo mais usado")}</span>
                <span className="text-[13px] font-medium text-[#0F172A]">
                  {champion.deviceDominant} ({champion.devicePercentage}%)
                </span>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Card 2: Outros Horários Recomendados */}
        <Card className="border border-[#E2E8FF] rounded-[12px] bg-white flex flex-col min-h-[280px]">
          <CardContent className="px-[22px] py-[16px] flex-1 flex flex-col gap-[12px]">
            <MetricCardHeader
              icon={TrendingUp}
              title={translateInterfaceText("Baseado no desempenho do período")}
              subtitle={translateInterfaceText("Horários alternativos recomendados")}
            />

            <div className="flex flex-col gap-[8px]">
              {otherTopWindows.slice(0, 3).map((window, i) => (
                <div
                  key={i}
                  className={`flex items-center justify-between px-4 py-3 rounded-[10px] min-h-[46px] transition-colors ${
                    i === 0
                      ? 'bg-[#EBF2FF] border border-[#3273FF]'
                      : 'bg-[#F7F9FF] border border-[#E2E8FF]'
                  }`}
                >
                  <div className="flex items-center gap-2">
                    <span className="text-[12px] font-normal text-[#6B7280]">{i + 1}º</span>
                    <span className="text-[13px] font-normal text-[#0F172A]">
                      {window.weekdayLabel} • {window.hourLabel}
                    </span>
                  </div>
                  <span className="text-[13px] font-medium text-[#0F172A]">
                    {window.value.toLocaleString(getInterfaceLanguage())}
                  </span>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Card 3: Melhor Horário por Dispositivo */}
        <Card className="border border-[#E2E8FF] rounded-[12px] bg-white flex flex-col min-h-[280px]">
          <CardContent className="px-[22px] py-[16px] flex-1 flex flex-col gap-[12px]">
            <MetricCardHeader
              icon={Smartphone}
              title={translateInterfaceText("Horários ideais por tipo de acesso")}
              subtitle={translateInterfaceText("Comparação dos melhores horários por device")}
            />

            <div className="flex flex-col gap-[8px]">
              <div className="flex items-center justify-between px-4 py-3 bg-[#F7F9FF] rounded-[10px] border border-[#E2E8FF] min-h-[46px]">
                <span className="text-[13px] font-medium text-[#0F172A]">{translateInterfaceText("Desktop")}</span>
                <span className="inline-flex px-3 py-1 rounded-full border border-[#93C5FD] text-[12px] font-medium text-[#2D6CF6]">
                  {deviceBestWindows.desktop}
                </span>
              </div>
              <div className="flex items-center justify-between px-4 py-3 bg-[#F7F9FF] rounded-[10px] border border-[#E2E8FF] min-h-[46px]">
                <span className="text-[13px] font-medium text-[#0F172A]">{translateInterfaceText("Mobile")}</span>
                <span className="inline-flex px-3 py-1 rounded-full border border-[#93C5FD] text-[12px] font-medium text-[#2D6CF6]">
                  {deviceBestWindows.mobile}
                </span>
              </div>
              <div className="flex items-center justify-between px-4 py-3 bg-[#F7F9FF] rounded-[10px] border border-[#E2E8FF] min-h-[46px]">
                <span className="text-[13px] font-medium text-[#0F172A]">{translateInterfaceText("Tablet")}</span>
                <span className="inline-flex px-3 py-1 rounded-full border border-[#93C5FD] text-[12px] font-medium text-[#2D6CF6]">
                  {deviceBestWindows.tablet}
                </span>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
